import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "qhearts-cookie-consent-v1";
const CookieContext = createContext(null);

const DEFAULT_CONSENT = {
  necessary: true,
  analytics: false,
  marketing: false,
  decidedAt: null,
};

function loadConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_CONSENT, ...parsed, necessary: true };
  } catch (err) {
    console.warn("Cookie-Einwilligung konnte nicht gelesen werden:", err);
    return null;
  }
}

function persistConsent(consent) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch (err) {
    console.warn("Cookie-Einwilligung konnte nicht gespeichert werden:", err);
  }
}

export function CookieProvider({ children }) {
  const [consent, setConsent] = useState(() => loadConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);

  const hasDecided = !!consent?.decidedAt;

  // Reload consent when another tab changes it
  useEffect(() => {
    const handler = (e) => {
      if (e.key === STORAGE_KEY) {
        setConsent(loadConsent());
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const decide = (next) => {
    const merged = {
      ...DEFAULT_CONSENT,
      ...next,
      necessary: true,
      decidedAt: new Date().toISOString(),
    };
    persistConsent(merged);
    setConsent(merged);
    setSettingsOpen(false);
  };

  const acceptAll = () =>
    decide({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () =>
    decide({ necessary: true, analytics: false, marketing: false });
  const reset = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      console.warn("Cookie-Einwilligung konnte nicht zurückgesetzt werden:", err);
    }
    setConsent(null);
  };

  const value = useMemo(
    () => ({
      consent: consent || DEFAULT_CONSENT,
      hasDecided,
      settingsOpen,
      openSettings: () => setSettingsOpen(true),
      closeSettings: () => setSettingsOpen(false),
      decide,
      acceptAll,
      rejectAll,
      reset,
    }),
    [consent, hasDecided, settingsOpen]
  );

  return (
    <CookieContext.Provider value={value}>{children}</CookieContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieContext);
  if (!ctx) throw new Error("useCookieConsent must be used within CookieProvider");
  return ctx;
}
